import React, { useState, useRef, useEffect } from "react";
import SvgMapper from "../../features/common/SvgMapper";
import VideoControl from "./VideoControl";
import MicrophoneControl from "./MicrophoneControl";
import AudioControl from "./AudioControl";
import VideoPreview from "./VideoPreview";
import SystemCheck from "./SystemCheck";

const InterviewCameraPreview = ({
  cameraStatusText = "Camera Active",
  caption = "You look great! Make sure you're well-lit",
  onValidationChange,
}) => {
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [isMicEnabled, setIsMicEnabled] = useState(false);
  const [cameraStatus, setCameraStatus] = useState("Not Started");
  const [micStatus, setMicStatus] = useState("Not Started");
  const [audioStatus, setAudioStatus] = useState("Not Started");
  const [internetStatus, setInternetStatus] = useState("Not Started");
  const [error, setError] = useState("");
  const isProcessingRef = useRef(false);
  const playPromiseRef = useRef(null);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const audioTestRef = useRef(null);

  useEffect(() => {
    const checkInternetConnection = () => {
      if (navigator.onLine) {
        setInternetStatus("Ready");
      } else {
        setInternetStatus("Not Started");
      }
    };

    checkInternetConnection();

    const handleOnline = () => {
      setInternetStatus("Ready");
    };
    const handleOffline = () => {
      setInternetStatus("Not Started");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      const audioTrack = streamRef.current.getAudioTracks()[0];
      
      if (videoTrack) {
        setCameraStatus(videoTrack.enabled ? "Ready" : "Not Started");
      } else {
        setCameraStatus("Not Started");
      }
      if (audioTrack) {
        setMicStatus(audioTrack.enabled ? "Ready" : "Not Started");
      } else {
        setMicStatus("Not Started");
      }
    } else {
      if (!isVideoEnabled) {
        setCameraStatus("Not Started");
      }
      if (!isMicEnabled) {
        setMicStatus("Not Started");
      }
    }
  }, [isVideoEnabled, isMicEnabled]);

  useEffect(() => {
    const isValid = 
      cameraStatus === "Ready" &&
      micStatus === "Ready" &&
      audioStatus === "Ready" &&
      internetStatus === "Ready";
    
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [cameraStatus, micStatus, audioStatus, internetStatus, onValidationChange]);

  const startMediaStream = async (enableVideo, enableAudio, preserveVideo = false) => {
    if (isProcessingRef.current) {
      return;
    }
    
    isProcessingRef.current = true;
    
    try {
      setError("");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: enableVideo,
        audio: enableAudio
      });
      
      if (preserveVideo && streamRef.current) {
        const existingVideoTrack = streamRef.current.getVideoTracks()[0];
        const newAudioTrack = stream.getAudioTracks()[0];
        
        if (existingVideoTrack && newAudioTrack) {
          streamRef.current.addTrack(newAudioTrack);
          stream.getVideoTracks().forEach(track => track.stop());
          isProcessingRef.current = false;
          return;
        }
      }
      
      streamRef.current = stream;
      
      if (videoRef.current && enableVideo) {
        if (playPromiseRef.current) {
          playPromiseRef.current.catch(() => {});
          playPromiseRef.current = null;
        }
        
        if (videoRef.current.srcObject) {
          videoRef.current.srcObject = null;
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        videoRef.current.srcObject = stream;
        
        try {
          const playPromise = videoRef.current.play();
          playPromiseRef.current = playPromise;
          
          if (playPromise !== undefined) {
            await playPromise;
          }
          playPromiseRef.current = null;
        } catch (playError) {
          if (playError.name !== 'AbortError' && playError.name !== 'NotAllowedError') {
            console.warn("Video play interrupted:", playError);
          }
          playPromiseRef.current = null;
        }
      }
      
      if (enableVideo) {
        setCameraStatus("Ready");
      } else {
        setCameraStatus("Not Started");
      }
      if (enableAudio) {
        setMicStatus("Ready");
      } else {
        setMicStatus("Not Started");
      }
    } catch (err) {
      console.error("Error accessing media devices:", err);
      setError(err.message || "Failed to access camera/microphone");
      if (enableVideo) {
        setCameraStatus("Error");
        setIsVideoEnabled(false);
      }
      if (enableAudio) {
        setMicStatus("Error");
        setIsMicEnabled(false);
      }
    } finally {
      isProcessingRef.current = false;
    }
  };

  const stopMediaStream = () => {
    if (playPromiseRef.current) {
      playPromiseRef.current.catch(() => {}); 
      playPromiseRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const toggleVideo = async () => {
    if (isProcessingRef.current) {
      return;
    }
    
    const newVideoState = !isVideoEnabled;
    
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      const audioTrack = streamRef.current.getAudioTracks()[0];
      
      if (newVideoState) {
        if (videoTrack) {
          videoTrack.enabled = true;
          setIsVideoEnabled(true);
          setCameraStatus("Ready");
        } else {
          stopMediaStream();
          setIsVideoEnabled(true);
          await startMediaStream(true, isMicEnabled);
        }
      } else {
        if (videoTrack) {
          videoTrack.stop(); 
          setIsVideoEnabled(false);
          setCameraStatus("Not Started");
          if (isMicEnabled && audioTrack) {
            stopMediaStream();
            await startMediaStream(false, true);
          } else if (!isMicEnabled) {
            stopMediaStream();
          }
        } else {
          setIsVideoEnabled(false);
          setCameraStatus("Not Started");
        }
      }
    } else if (newVideoState) {
      setIsVideoEnabled(true);
      await startMediaStream(true, isMicEnabled);
    } else {
      setIsVideoEnabled(false);
      setCameraStatus("Not Started");
    }
  };

  const toggleMic = async () => {
    if (isProcessingRef.current) {
      return;
    }
    
    const newMicState = !isMicEnabled;
    
    if (newMicState) {
      if (streamRef.current) {
        const videoTrack = streamRef.current.getVideoTracks()[0];
        const audioTrack = streamRef.current.getAudioTracks()[0];
        
        if (audioTrack) {
          audioTrack.enabled = true;
          setIsMicEnabled(true);
          setMicStatus("Ready");
        } else if (videoTrack && isVideoEnabled) {
          try {
            setIsMicEnabled(true);
            const newStream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: true
            });
            
            const oldStream = streamRef.current;
            streamRef.current = newStream;
            
            if (videoRef.current) {
              if (playPromiseRef.current) {
                playPromiseRef.current.catch(() => {}); 
                playPromiseRef.current = null;
              }
              
              if (videoRef.current.srcObject) {
                videoRef.current.srcObject = null;
                await new Promise(resolve => setTimeout(resolve, 50));
              }
              
              videoRef.current.srcObject = newStream;
              
              try {
                const playPromise = videoRef.current.play();
                playPromiseRef.current = playPromise;
                
                if (playPromise !== undefined) {
                  await playPromise;
                }
                playPromiseRef.current = null;
              } catch (playError) {
                if (playError.name !== 'AbortError' && playError.name !== 'NotAllowedError') {
                  console.warn("Video play interrupted:", playError);
                }
                playPromiseRef.current = null;
              }
            }
            
            setMicStatus("Ready");
            
            if (oldStream) {
              oldStream.getTracks().forEach(track => track.stop());
            }
          } catch (err) {
            console.error("Error adding audio to video stream:", err);
            setError(err.message || "Failed to enable microphone");
            setIsMicEnabled(false);
            setMicStatus("Error");
          }
        } else {
          stopMediaStream();
          setIsMicEnabled(true);
          await startMediaStream(false, true);
        }
      } else {
        setIsMicEnabled(true);
        await startMediaStream(isVideoEnabled, true);
      }
      return;
    }
    
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      const audioTrack = streamRef.current.getAudioTracks()[0];
      
      if (audioTrack) {
        audioTrack.stop();
        setIsMicEnabled(false);
        setMicStatus("Not Started");
        
        if (isVideoEnabled && videoTrack) {
        } else if (!isVideoEnabled) {
          stopMediaStream();
        }
      } else {
        setIsMicEnabled(false);
        setMicStatus("Not Started");
      }
    }
  };

  const toggleAudio = async () => {
    const newAudioState = !isAudioEnabled;
    
    if (newAudioState) {
      try {
        setIsAudioEnabled(true);
        
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        gainNode.gain.value = 0.1;
        oscillator.frequency.value = 440;
        oscillator.type = 'sine';
        
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.1);
        
        setTimeout(() => {
          if (audioContext.state === 'running') {
            setAudioStatus("Ready");
          } else {
            setAudioStatus("Ready"); 
          }
          audioContext.close();
        }, 150);
        
        setAudioStatus("Ready");
      } catch (err) {
        console.error("Error testing audio output:", err);
        setError("Failed to test audio output");
        setIsAudioEnabled(false);
        setAudioStatus("Error");
      }
    } else {
      setIsAudioEnabled(false);
      setAudioStatus("Not Started");
      
      if (audioTestRef.current) {
        audioTestRef.current.pause();
        audioTestRef.current = null;
      }
    }
  };

  const checks = [
    { label: "Camera Access", status: cameraStatus },
    { label: "Microphone Access", status: micStatus },
    { label: "Audio Output", status: audioStatus },
    { label: "Internet Connection", status: internetStatus },
  ];

  return (
    <>
      <div className="space-x-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          {SvgMapper.camera("w-6 h-6 mr-2 text-gray-600")}
          Camera &amp; Audio Check
        </h3>

        <VideoPreview
          videoRef={videoRef}
          isVideoEnabled={isVideoEnabled}
          isMicEnabled={isMicEnabled}
          streamRef={streamRef}
          cameraStatusText={cameraStatusText}
          caption={caption}
        />

        {error && (
          <div className="mt-2 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center justify-center space-x-4 mt-4">
          <VideoControl isEnabled={isVideoEnabled} onToggle={toggleVideo} />
          <MicrophoneControl isEnabled={isMicEnabled} onToggle={toggleMic} />
          <AudioControl isEnabled={isAudioEnabled} onToggle={toggleAudio} />
        </div>

        <SystemCheck checks={checks} />
      </div>
    </>
  );
};

export default InterviewCameraPreview;
